import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import heroImage from "@/assets/hero-macbook.jpg";
import { useI18n } from "@/i18n";
import { useDownload } from "@/hooks/useDownload";
import { useEffect } from "react";
import { AiFillApple } from "react-icons/ai";

const Hero = () => {
  const { language } = useI18n();
  const { isLoading, downloadLatest, latestVersion, getVersionInfo } = useDownload();

  useEffect(() => {
    getVersionInfo();
  }, [getVersionInfo]);

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-white">
      <div className="container relative z-10 mx-auto px-4 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-1000">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-gray-900 leading-tight">
                {language === 'en' ? (
                  <>Your AI<br />On Your Mac<br />Under Your Control</>
                ) : (
                  <>你的 AI<br />在你的 Mac 上<br />由你掌控</>
                )}
              </h1>
              <p className="text-lg md:text-xl text-gray-500 mb-8 max-w-lg">
                {language === 'en'
                  ? 'The privacy-first AI platform built for Apple Silicon. Chat, create, and automate — everything stays on your device.'
                  : '为 Apple Silicon 打造的隐私优先 AI 平台。对话、创建、自动化 —— 一切都留在你的设备上。'}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <Button
                  size="lg"
                  className="text-base px-8 bg-[#1E88C8] hover:bg-[#1670A8] text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                  onClick={downloadLatest}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" />
                      {language === 'en' ? 'Loading...' : '加载中...'}
                    </>
                  ) : (
                    <>
                      <AiFillApple className="mr-1" />
                      {language === 'en' ? `Download Estha for Mac ${latestVersion?.version || ''}` : `下载 Estha for Mac ${latestVersion?.version || ''}`}
                    </>
                  )}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base px-8 border-gray-300 text-gray-700 hover:bg-gray-50"
                  onClick={() => document.getElementById('apps')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {language === 'en' ? 'Browse Apps' : '浏览应用'}
                </Button>
              </div>

              <div className="space-y-1.5">
                <p className="text-sm text-gray-500">
                  {language === 'en'
                    ? '⚡ Runs on any Mac with Apple Silicon (M2 or later) · 16GB RAM minimum'
                    : '⚡ 支持所有 Apple Silicon Mac（M2 及以上）· 最低 16GB 内存'}
                </p>
                <p className="text-xs text-gray-400">
                  {language === 'en'
                    ? 'Premium experience on 32GB+ devices with an enhanced AI model'
                    : '32GB+ 设备可解锁更强大的 AI 模型'}
                </p>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300">
              <div className="relative rounded-2xl overflow-hidden shadow-card">
                <img
                  src={heroImage}
                  alt="Estha AI running on MacBook Pro"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
